import { useCallback, useEffect, useRef } from 'react'
import { normalizeCountry } from '~/shared/lib/countries'
import { storageManager } from '~/shared/lib/storage-manager'

interface FormPersistenceOptions {
  storageKey: string
  enabled?: boolean
  debounceMs?: number
}

interface PersistedFormState<T> {
  values: T
  scrollPosition: number
  timestamp: number
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000

function normalizeCountryFields<T extends Record<string, unknown>>(values: T): T {
  const normalized: Record<string, unknown> = { ...values }

  const location = normalized.location as { country?: string } | undefined
  if (location?.country) {
    normalized.location = {
      ...location,
      country: normalizeCountry(location.country),
    }
  }

  if (typeof normalized.country === 'string' && normalized.country) {
    normalized.country = normalizeCountry(normalized.country)
  }

  return normalized as T
}

export function useFormPersistence<T extends Record<string, unknown> = Record<string, unknown>>(
  // biome-ignore lint/suspicious/noExplicitAny: TanStack Form API has too many generic params to reasonably type here
  form: any,
  options: FormPersistenceOptions,
) {
  const { storageKey, enabled = true, debounceMs = 500 } = options
  const debounceTimerRef = useRef<NodeJS.Timeout>(undefined)
  const isRestoringRef = useRef(false)

  const saveFormState = useCallback(() => {
    if (!enabled) return

    const state: PersistedFormState<T> = {
      values: form.state.values,
      scrollPosition: window.scrollY,
      timestamp: Date.now(),
    }
    storageManager.setSession(storageKey, state)
  }, [form, enabled, storageKey])

  const loadFormState = useCallback((): PersistedFormState<T> | null => {
    if (!enabled) return null

    const state = storageManager.getSession<PersistedFormState<T>>(storageKey)
    if (!state) return null

    const age = Date.now() - state.timestamp
    if (age > MAX_AGE_MS) {
      storageManager.removeSession(storageKey)
      return null
    }

    state.values = normalizeCountryFields(state.values)
    return state
  }, [enabled, storageKey])

  const clearFormState = () => {
    storageManager.removeSession(storageKey)
  }

  const restoreRef = useRef({ form, loadFormState })
  restoreRef.current = { form, loadFormState }

  useEffect(() => {
    const savedState = restoreRef.current.loadFormState()
    if (savedState?.values) {
      isRestoringRef.current = true

      Object.entries(savedState.values).forEach(([key, value]) => {
        restoreRef.current.form.setFieldValue(key, value)
      })

      setTimeout(() => {
        window.scrollTo({
          top: savedState.scrollPosition,
          behavior: 'smooth',
        })
        isRestoringRef.current = false
      }, 100)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    const subscription = form.store.subscribe(() => {
      if (isRestoringRef.current) return

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        saveFormState()
      }, debounceMs)
    })

    return () => {
      subscription.unsubscribe()
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [form, enabled, debounceMs, saveFormState])

  useEffect(() => {
    if (!enabled) return

    const handleScroll = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        saveFormState()
      }, debounceMs)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [enabled, debounceMs, saveFormState])

  return {
    loadFormState,
    clearFormState,
    hasSavedState: () => storageManager.getSession(storageKey) !== null,
  }
}
